import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { DxcIconComponent } from '../icon/dxc-icon.component';
import {
    DXC_SECTION_HEAD_VARIANT,
    DxcIconName,
    DxcSectionHeadTone,
    DxcSectionHeadVariant,
} from '../dxc.types';

/**
 * Encabezado de sección — el escalón entre `dxc-page-header`, que titula una
 * pantalla, y `.dxc-card__head`, que titula una tarjeta. Un formulario largo se
 * divide por dentro en bloques —origen, destino, retorno— y no había nada
 * para eso: lo que había eran pastillas de color sólido con gama propia, que se
 * leían como estado sin serlo.
 *
 * ## Por qué es un componente y no cuatro clases
 *
 * La forma del encabezado es una **prop**, no una combinación de clases que hay
 * que acertar en cada una de las ~40 llamadas. `variant` sale por defecto de
 * `DXC_SECTION_HEAD_VARIANT`, así que cambiar la forma de todo el producto es
 * cambiar una constante — y una vista que necesite desviarse lo hace con
 * `[variant]` sin arrastrar a las demás.
 *
 * Las cinco formas se sostienen sobre el **mismo marcado**. No es una
 * casualidad afortunada: es el requisito. Si una forma necesitara su propio
 * HTML dejaría de poder ser una prop, y volveríamos a tener cuatro plantillas
 * que hay que migrar a mano cada vez que el sistema cambia de opinión.
 *
 * ## El sangrado
 *
 * `marbete` sangra hasta el canto del contenedor leyendo `--dxc-bleed`. Es lo
 * único que no puede deducir —CSS no sabe leer el `padding` de un ancestro—, y
 * por eso lo declara el contenedor y no la llamada. Sin declararlo vale 0 y el
 * marbete se cierra por los dos lados: la degradación es una forma correcta,
 * no un roto.
 *
 * @example
 * <dxc-section-head icon="punto-origen" tone="accent" title="Origen / Remitente" />
 *
 * <dxc-section-head icon="container" title="Contenedor #1" meta="Sin número">
 *   <button class="dxc-btn dxc-btn--danger-ghost dxc-btn--icon dxc-btn--sm" aria-label="Quitar">
 *     <dxc-icon name="x-circle" />
 *   </button>
 * </dxc-section-head>
 */
@Component({
    standalone: true,
    selector: 'dxc-section-head',
    imports: [DxcIconComponent, NgTemplateOutlet],
    template: `
        <span class="dxc-section-head__icon">
            @if (ordinal !== null) { <span class="dxc-section-head__ordinal">{{ ordinal }}</span> }
            @else { <dxc-icon [name]="icon"></dxc-icon> }
        </span>

        @if (desc) {
            <span class="dxc-section-head__body">
                <ng-container *ngTemplateOutlet="titleTpl"></ng-container>
                <span class="dxc-section-head__desc">{{ desc }}</span>
            </span>
        } @else {
            <ng-container *ngTemplateOutlet="titleTpl"></ng-container>
        }

        @if (meta) { <span class="dxc-section-head__meta">{{ meta }}</span> }

        <span class="dxc-section-head__actions"><ng-content></ng-content></span>

        <!-- El nivel del encabezado cambia la etiqueta, no la pinta: el rótulo
             de una sección de página es un heading de verdad y el de un bloque
             dentro de un diálogo no lo es. Se resuelve una vez aquí para no
             repetir el bloque entero en las dos ramas de arriba. -->
        <ng-template #titleTpl>
            @switch (heading) {
                @case ('h2') { <h2 class="dxc-section-head__title">{{ title }}</h2> }
                @case ('h3') { <h3 class="dxc-section-head__title">{{ title }}</h3> }
                @case ('h4') { <h4 class="dxc-section-head__title">{{ title }}</h4> }
                @default     { <span class="dxc-section-head__title">{{ title }}</span> }
            }
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcSectionHeadComponent {

    /** Quien distingue un bloque de otro es el icono; el tono solo ayuda a barrer. */
    @Input() icon: DxcIconName = 'info';

    /** Ocupa el sitio del glifo cuando el bloque va numerado. Gana al icono. */
    @Input() ordinal: number | null = null;

    @Input() title = '';

    /** La línea de apoyo. Cae fuera de la píldora, bajo el rótulo. */
    @Input() desc = '';

    /** El dato de apoyo, a la derecha del rótulo. Es lo primero que se recorta. */
    @Input() meta = '';

    /** Clasificación, no estado (§5). */
    @Input() tone: DxcSectionHeadTone = 'neutral';

    /** La forma. Por defecto, la del producto entero. */
    @Input() variant: DxcSectionHeadVariant = DXC_SECTION_HEAD_VARIANT;

    /** `span` cuando el rótulo no encabeza nada en el documento. */
    @Input() heading: 'h2' | 'h3' | 'h4' | 'span' = 'span';

    @HostBinding('class.dxc-section-head') readonly hostClass = true;

    @HostBinding('attr.data-variant')
    get variantAttr() { return this.variant; }

    @HostBinding('class.dxc-section-head--accent')  get isAccent()  { return this.tone === 'accent'; }
    @HostBinding('class.dxc-section-head--info')    get isInfo()    { return this.tone === 'info'; }
    @HostBinding('class.dxc-section-head--success') get isSuccess() { return this.tone === 'success'; }
    @HostBinding('class.dxc-section-head--warning') get isWarning() { return this.tone === 'warning'; }
    @HostBinding('class.dxc-section-head--danger')  get isDanger()  { return this.tone === 'danger'; }
}
